import React, { useState, useEffect, useMemo } from "react";
import useStreamChat from "./home/useStreamChat";
import ChartAwareMarkdown from "./ChartAwareMarkdown";
import { months } from "./sidepane/SidePanel";
import "./styles/PlaceAiPanelStyle.css";

const base_url = process.env.REACT_APP_BASE_URL || "https://ada.acmad.org";

// Maps dekad labels to API-expected day values
const DEKAD_MAP = { "1st": "01", "2nd": "11", "3rd": "21" };

// Matches the SQL array order used in handle_point.py gpcc_average_query
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sept","Oct","Nov","Dec"];

function buildChartData(cellValues) {
  if (!cellValues) return {};
  const byYearKey = (a, b) =>
    a.year !== b.year ? a.year - b.year : a.key.localeCompare(b.key);

  const cdi_series = [];
  for (const [yr, data] of Object.entries(cellValues.cdi_data || {})) {
    for (const [key, val] of Object.entries(data || {})) {
      if (key.startsWith("cdi_") && val !== null) {
        cdi_series.push({ year: parseInt(yr, 10), key, value: val });
      }
    }
  }
  cdi_series.sort(byYearKey);

  const spi_series = [];
  for (const [yr, data] of Object.entries(cellValues.spi_data || {})) {
    for (const [key, val] of Object.entries(data || {})) {
      if (key.startsWith("spi_") && val !== null) {
        spi_series.push({ year: parseInt(yr, 10), key, value: val });
      }
    }
  }
  spi_series.sort(byYearKey);

  const fapar_series = [];
  for (const [yr, data] of Object.entries(cellValues.fapar_anomaly_data || {})) {
    for (const [key, val] of Object.entries(data || {})) {
      if (key.startsWith("anomaly_") && val !== null) {
        fapar_series.push({ year: parseInt(yr, 10), key, value: val });
      }
    }
  }
  fapar_series.sort(byYearKey);

  const gpccAvg = cellValues.gpcc_avg_data || {};
  const rainfall_vs_average = [];
  for (const [yr, data] of Object.entries(cellValues.gpcc_data || {})) {
    for (const [key, val] of Object.entries(data || {})) {
      const m = key.match(/^mon_rain_(\d{2})$/);
      if (m) {
        const label = MONTH_LABELS[parseInt(m[1], 10) - 1] || `M${m[1]}`;
        rainfall_vs_average.push({
          year: parseInt(yr, 10),
          month: label,
          actual: val,
          average: gpccAvg[label] ?? null,
        });
      }
    }
  }
  rainfall_vs_average.sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return MONTH_LABELS.indexOf(a.month) - MONTH_LABELS.indexOf(b.month);
  });

  return { cdi_series, spi_series, fapar_series, rainfall_vs_average };
}

function PlaceAiSkeleton({ lines = 3 }) {
  return (
    <div className="place-ai-skeleton">
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="place-ai-skeleton-line"
          style={{ width: i % 2 === 0 ? "88%" : "65%" }}
        />
      ))}
    </div>
  );
}

export default function PlaceAiPanel({
  clicked_latlong,
  dates,
  setAdmin_details,
  setShow_generate_report,
}) {
  const { response, isLoading, error, startStream } = useStreamChat();
  const [adminInfo, setAdminInfo] = useState(null);
  const [cellValues, setCellValues] = useState(null);
  const localChartData = useMemo(() => buildChartData(cellValues), [cellValues]);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [analysisStarted, setAnalysisStarted] = useState(false);

  // Fetch admin info whenever the clicked location or dates change
  useEffect(() => {
    if (clicked_latlong?.lat == null || clicked_latlong?.lon == null) return;
    setAnalysisStarted(false);
    setCellValues(null);
    setFetchingInfo(true);

    const monthInt = parseInt(months[dates.month] || "1", 10);
    const dekadStr = DEKAD_MAP[dates.dekad] || "01";
    const url =
      `${base_url}/api/data_api/geojson/getcellvalues` +
      `?lat=${clicked_latlong.lat}&lon=${clicked_latlong.lon}` +
      `&year=${dates.year}&month=${monthInt}&dekad=${dekadStr}`;

    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const ai = data?.admin_info;
        setAdminInfo(ai || {});
        setCellValues(data?.cell_values || null);
        if (ai) {
          const countryInfo = ai.admin_zero_info;
          const adminOneInfo = ai.admin_one_info;
          setAdmin_details({
            country: countryInfo?.country_name,
            region: adminOneInfo?.admin_one_name,
            country_code: countryInfo?.country_code,
            africa_region: countryInfo?.region_name,
            year: dates.year,
            month: dates.month,
            dekad: dates.dekad,
          });
          // Reporting.js reads this from localStorage (opened in a new tab) to get
          // country_bounds/country_name for the report maps and title.
          try {
            localStorage.setItem(
              "sharedData",
              JSON.stringify({ admin_info: data?.admin_info })
            );
          } catch (e) {
            console.warn("Unable to persist sharedData to localStorage", e);
          }
          setShow_generate_report(true);
        } else {
          setShow_generate_report(false);
        }
      })
      .catch(() => {
        setAdminInfo({});
        setShow_generate_report(false);
      })
      .finally(() => setFetchingInfo(false));
  }, [clicked_latlong, dates]); // eslint-disable-line react-hooks/exhaustive-deps

  const CDI_CLASSES = ["No Drought", "Watch", "Warning", "Alert"];

  const handleAnalyze = () => {
    const adminName = adminInfo?.admin_one_info?.admin_one_name || "";
    const country = adminInfo?.admin_zero_info?.country_name || "";

    const monthInt = parseInt(months[dates.month] || "1", 10);
    const MM = String(monthInt).padStart(2, "0");
    const dekadStr = DEKAD_MAP[dates.dekad] || "01";
    // CDI uses prefix "cdi_MMDD"; SMA and FAPAR use "anomaly_MMDD"
    const cdiKey    = `cdi_${MM}${dekadStr}`;
    const anomalyKey = `anomaly_${MM}${dekadStr}`;
    const yearStr = String(dates.year);

    const cdiRaw  = cellValues?.cdi_data?.[yearStr]?.[cdiKey] ?? null;
    const smaRaw  = cellValues?.sma_data?.[yearStr]?.[anomalyKey] ?? null;
    const fapar   = cellValues?.fapar_anomaly_data?.[yearStr]?.[anomalyKey] ?? null;
    const spi1    = cellValues?.spi_data?.[yearStr]?.[`spi_${MM}01`] ?? null;
    const spi3    = cellValues?.spi_data?.[yearStr]?.[`spi_${MM}03`] ?? null;
    const rain    = cellValues?.gpcc_data?.[yearStr]?.[`mon_rain_${MM}`] ?? null;
    const avgRain = cellValues?.gpcc_avg_data?.[dates.month] ?? null;
    const cdiClass =
      cdiRaw !== null
        ? (CDI_CLASSES[Math.round(cdiRaw)] ?? String(cdiRaw))
        : "N/A";

    const contextData = {
      admin_one: adminName,
      country,
      lat: clicked_latlong.lat,
      lon: clicked_latlong.lon,
      month: dates.month,
      year: dates.year,
      dekad: dates.dekad,
      cdi_value: cdiRaw,
      cdi_class: cdiClass,
      sma_value: smaRaw,
      fapar_value: fapar,
      spi1_value: spi1,
      spi3_value: spi3,
      rainfall: rain,
      avg_rainfall: avgRain,
    };

    setAnalysisStarted(true);
    startStream(
      `Analyze drought conditions for the ${adminName || "selected"} region of ${country || "Africa"}.`,
      { contextData }
    );
  };

  if (clicked_latlong?.lat == null || clicked_latlong?.lon == null) {
    return (
      <div className="place-ai-panel place-ai-empty">
        <p className="is-size-6 is-family-code has-text-centered">
          Click on the map to analyse a location
        </p>
      </div>
    );
  }

  const adminName = adminInfo?.admin_one_info?.admin_one_name;
  const countryName = adminInfo?.admin_zero_info?.country_name;

  return (
    <div className="place-ai-panel">
      {/* Skeleton while fetching admin info */}
      {fetchingInfo && <PlaceAiSkeleton lines={2} />}

      {/* State 2 & 3: location card */}
      {adminInfo && (
        <div className="place-ai-location-card">
          <span className="place-ai-location-name">
            {adminName}
            {countryName ? ` · ${countryName}` : ""}
          </span>
          <span className="place-ai-location-meta">
            {dates.month} {dates.year} · Dekad {dates.dekad}
          </span>
        </div>
      )}

      {/* State 2: Analyze button */}
      {!analysisStarted && adminInfo && (
        <div className="place-ai-btn-wrap">
          <button
            className="button is-small is-rounded is-outlined place-ai-btn"
            onClick={handleAnalyze}
          >
            ✦ Analyze with AI
          </button>
        </div>
      )}

      {/* State 3: streaming response */}
      {analysisStarted && (
        <div className="place-ai-response">
          <hr className="place-ai-divider" />
          {isLoading && !response && <PlaceAiSkeleton lines={4} />}
          {response && <ChartAwareMarkdown text={response} chartData={localChartData} />}
          {isLoading && <span className="streaming-dot" />}
        </div>
      )}

      {/* Chat with AI button — shown after streaming completes */}
      {analysisStarted && !isLoading && response && (
        <div className="place-ai-footer">
          {/* Served by the standalone Next.js chat app behind nginx — real nav. */}
          <a
            href="/drought-chat"
            className="button is-small is-rounded is-outlined place-ai-btn"
          >
            ✦ Chat with AI for more
          </a>
        </div>
      )}

      {error && (
        <p className="has-text-danger is-size-7" style={{ marginTop: 8 }}>
          {error}
        </p>
      )}
    </div>
  );
}
